
import React, { useEffect } from "react";
import { FormFieldType } from "@/types/form";
import { useForm } from "../../../context/FormContext";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { FieldLabelWithInfo } from "./FieldLabelWithInfo";
import { KnowledgeRatingInfo, SkillsRatingInfo, AttitudeRatingInfo } from "../components";
import { useTranslation } from "react-i18next";
import { tField } from "../utils/i18nField";

interface RadioFieldProps {
  element: FormFieldType;
}

export const RadioField: React.FC<RadioFieldProps> = ({ element }) => {
  const { formData, updateField, setError, clearError } = useForm();
  const { t } = useTranslation();
  const fieldValue = formData[element.name || ""] as string || "";

  const handleChange = (value: string) => {
    if (!element.name) return;
    updateField(element.name, value);
    
    // Check the corresponding behavior field when a rating is selected
    if (element.name === "knowledge_rating") {
      validateTextLength("knowledge_behavior");
    } else if (element.name === "skills_rating") {
      validateTextLength("skills_behavior");
    } else if (element.name === "attitude_rating") {
      validateTextLength("attitude_behavior");
    }
  };
  
  // Validate that the text length is more than 50 characters
  const validateTextLength = (fieldName: string) => {
    const behaviorText = formData[fieldName] as string || "";
    
    if (behaviorText.length < 50) {
      setError(fieldName, t('form.fields.behaviorMinChars', { count: behaviorText.length }));
    } else {
      clearError(fieldName);
    }
  };
  
  // Validate on initial load
  useEffect(() => {
    if (element.name === "knowledge_rating" && fieldValue) {
      validateTextLength("knowledge_behavior");
    } else if (element.name === "skills_rating" && fieldValue) {
      validateTextLength("skills_behavior");
    } else if (element.name === "attitude_rating" && fieldValue) {
      validateTextLength("attitude_behavior");
    }
  }, [fieldValue]);

  // Check if this is one of the special rating fields
  const isKnowledgeRating = element.name === "knowledge_rating";
  const isSkillsRating = element.name === "skills_rating";
  const isAttitudeRating = element.name === "attitude_rating";
  
  // Determine which info content to show
  let infoContent = null;
  if (isKnowledgeRating) {
    infoContent = <KnowledgeRatingInfo />;
  } else if (isSkillsRating) {
    infoContent = <SkillsRatingInfo />;
  } else if (isAttitudeRating) {
    infoContent = <AttitudeRatingInfo />;
  }
  
  return (
    <div className="space-y-3">
      <FieldLabelWithInfo element={element} infoContent={infoContent} />
      
      <RadioGroup
        value={fieldValue}
        onValueChange={handleChange}
        className="space-y-2"
      >
        {element.options?.map((option) => (
          <div key={String(option.value)} className="flex items-center space-x-2">
            <RadioGroupItem
              id={`${element.id}-${option.value}`}
              value={String(option.value)}
            />
            <Label htmlFor={`${element.id}-${option.value}`} className="cursor-pointer">
              {tField(t, option.label)}
            </Label>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
};

export default RadioField;
